/**
 * The billing reads' client cache — mirrors the server's snapshot semantics
 * (05 §2/§6): a 5-minute TTL is the missed-invalidation bound, and a failed
 * refetch serves the stale entry for 60 s before surfacing the error.
 *
 * One module-level Map keyed by cache key: every consumer of
 * `useEntitlement("storage")` shares the same entry, so a paywall card and
 * the settings section render from one read, and `invalidate()` after a
 * lifecycle action re-reads exactly once.
 */
import type { EntitlementEntry, UsageResponse } from "../types";

interface CacheEntry {
  at: number;
  value: unknown;
}

const TTL_MS = 5 * 60_000;
const STALE_WHILE_ERROR_MS = 60_000;

const globalForBillingCache = globalThis as unknown as {
  __suiteBillingCache?: Map<string, CacheEntry>;
};
const cache: Map<string, CacheEntry> = globalForBillingCache.__suiteBillingCache ?? new Map();
globalForBillingCache.__suiteBillingCache = cache;

export const billingCacheKeys = {
  entitlements: "entitlements",
  usage: "usage",
  plans: "plans",
  entitlement: (serviceId: string) => `entitlement:${serviceId}`,
} as const;

export function readCached<T>(key: string): { value: T; fresh: boolean; staleOk: boolean } | null {
  const entry = cache.get(key);
  if (!entry) return null;
  const age = Date.now() - entry.at;
  return {
    value: entry.value as T,
    fresh: age < TTL_MS,
    staleOk: age < TTL_MS + STALE_WHILE_ERROR_MS,
  };
}

export function writeCached<T>(key: string, value: T): void {
  cache.set(key, { at: Date.now(), value });
}

/** Drop one key (after a lifecycle action) or everything. */
export function invalidateBillingCache(key?: string): void {
  if (key) cache.delete(key);
  else cache.clear();
}

export type EntitlementsReader = () => Promise<EntitlementEntry[]>;
export type UsageReader = () => Promise<UsageResponse>;

/** Shared in-flight dedupe: one request per key at a time, for every consumer. */
const inflight = new Map<string, Promise<unknown>>();

export function cachedRead<T>(key: string, reader: () => Promise<T>): Promise<T> {
  const cached = readCached<T>(key);
  if (cached?.fresh) return Promise.resolve(cached.value);
  const running = inflight.get(key);
  if (running) return running as Promise<T>;
  const promise = reader()
    .then((value) => {
      writeCached(key, value);
      return value;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, promise);
  return promise;
}
